import type {DashboardColumnDef, DashboardColumnKey} from '../constants/dashboardColumns';
import type {DashboardDateField} from '../hooks/useDashboardFilters';

/**
 * One exported ticket, keyed by column. Values are already display-formatted —
 * the export must read the same as the table it was taken from, so formatting
 * happens once, where the table does it, rather than twice with two roundings.
 */
export type DashboardCsvRow = Partial<Record<DashboardColumnKey, string | number | null>>;

export interface DownloadDashboardCsvOptions {
	from: string;
	to: string;
	dateField: DashboardDateField;
	/** The columns to export, in table order — normally the user's visible set. */
	columns: readonly DashboardColumnDef[];
	rows: readonly DashboardCsvRow[];
}

const DATE_FIELD_SLUGS: Record<DashboardDateField, string> = {
	created_at: 'created',
	last_activity_at: 'last-activity',
	resolved_at: 'resolved',
};

/**
 * Characters that make a spreadsheet treat a cell as a formula. A ticket subject
 * beginning with one would otherwise be executed on open, which is how a CSV
 * export becomes an injection vector. Prefixing with a single quote keeps the text
 * intact and inert.
 */
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];

export function escapeCsvValue(value: string | number | null | undefined): string {
	if (value === undefined || value === null) return '';
	let str = String(value);
	if (FORMULA_TRIGGERS.includes(str.charAt(0))) {
		str = `'${str}`;
	}
	if (/[",\n\r]/.test(str)) {
		return `"${str.replace(/"/g, '""')}"`;
	}
	return str;
}

/** The CSV text for a set of rows, exported for testing without touching the DOM. */
export function buildDashboardCsv(columns: readonly DashboardColumnDef[], rows: readonly DashboardCsvRow[]): string {
	// No leading metadata/comment line: spreadsheets read the first line as the
	// header row, so a "# exported ..." preamble shifts every column. The range and
	// date field go in the filename instead.
	const header = columns.map(col => escapeCsvValue(col.label)).join(',');
	const body = rows.map(row => columns.map(col => escapeCsvValue(row[col.key])).join(','));
	return [header, ...body].join('\n');
}

export function dashboardCsvFilename({
	from,
	to,
	dateField,
}: Pick<DownloadDashboardCsvOptions, 'from' | 'to' | 'dateField'>): string {
	return `ticket-dashboard-${DATE_FIELD_SLUGS[dateField]}-${from}-to-${to}.csv`;
}

/** Byte-order mark, as an escape: the literal character reads as stray whitespace. */
const UTF8_BOM = '\ufeff';

export function downloadDashboardCsv({from, to, dateField, columns, rows}: DownloadDashboardCsvOptions): void {
	const csv = buildDashboardCsv(columns, rows);
	// The BOM is what makes Excel read the file as UTF-8; without it, non-ASCII
	// requester names and subjects arrive mojibaked. Written as an escape rather
	// than the literal character, which reads as stray whitespace in the source.
	const blob = new Blob([`${UTF8_BOM}${csv}`], {type: 'text/csv;charset=utf-8;'});
	const url = URL.createObjectURL(blob);

	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = dashboardCsvFilename({from, to, dateField});
	anchor.style.display = 'none';
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}
